import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';
import {
  Calendar,
  Download,
  DollarSign,
  ShoppingBag,
  Heart,
  TrendingUp,
  PieChart as PieIcon,
  Clock,
  Loader2,
} from 'lucide-react';

const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
  { id: 'this_month', label: 'This month' },
  { id: 'all', label: 'All time' },
  { id: 'custom', label: 'Custom' },
];

const COLORS = ['#E0A830', '#0EA5E9', '#10B981', '#A855F7', '#F43F5E'];

const StatCard = ({ icon: Icon, label, value, sub, color = 'gold' }) => {
  const ringMap = {
    gold: 'bg-gold-50 text-gold-600 ring-gold-200',
    blue: 'bg-blue-50 text-blue-600 ring-blue-200',
    green: 'bg-emerald-50 text-emerald-600 ring-emerald-200',
    purple: 'bg-purple-50 text-purple-600 ring-purple-200',
    rose: 'bg-rose-50 text-rose-600 ring-rose-200',
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ring-1 ${ringMap[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      </div>
      <div className="text-2xl font-extrabold text-slate-900" data-testid={`insight-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>{value}</div>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
};

const Insights = ({ api, restaurant }) => {
  const { t } = useTranslation();
  const PERIODS_LOCAL = [
    { id: 'today', label: t('insights.today') },
    { id: '7d', label: t('insights.last_7') },
    { id: '30d', label: t('insights.last_30') },
    { id: 'this_month', label: t('insights.this_month') },
    { id: 'all', label: t('insights.all_time') },
    { id: 'custom', label: t('insights.custom') },
  ];
  const [period, setPeriod] = useState('7d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchData = async () => {
    if (!restaurant?.id) return;
    setLoading(true);
    try {
      const params = period === 'custom' && customStart && customEnd
        ? { start: customStart, end: `${customEnd}T23:59:59` }
        : { period };
      const res = await api.get(`/restaurants/${restaurant.id}/insights`, { params });
      setData(res.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load insights:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant?.id, period, customStart, customEnd]);

  const exportCsv = async () => {
    if (!restaurant?.id) return;
    setExporting(true);
    try {
      const params = period === 'custom' && customStart && customEnd
        ? { start: customStart, end: `${customEnd}T23:59:59` }
        : { period };
      const res = await api.get(`/restaurants/${restaurant.id}/insights/export.csv`, {
        params,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `nofee_orders_${restaurant.slug || 'restaurant'}_${period}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to export CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const summary = data?.summary || {};
  const daily = data?.daily_series || [];
  const topItems = data?.top_items || [];
  const byType = data?.by_order_type || [];
  const byHour = data?.by_hour || [];

  const maxHourOrders = useMemo(() => Math.max(1, ...byHour.map((h) => h.orders)), [byHour]);

  return (
    <div data-testid="insights-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{t("insights.title")}</h2>
          <p className="text-sm text-slate-500 mt-1">{t("insights.subtitle")}</p>
        </div>
        <button
          onClick={exportCsv}
          disabled={exporting || loading}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition-colors disabled:opacity-50"
          data-testid="insights-export-csv"
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Export CSV
        </button>
      </div>

      {/* Period selector */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="insights-period-selector">
        {PERIODS_LOCAL.map((p) => (
          <button
            key={p.id}
            onClick={() => setPeriod(p.id)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border-2 transition-all ${
              period === p.id
                ? 'bg-gold-500 text-white border-gold-500'
                : 'bg-white text-slate-700 border-slate-200 hover:border-gold-400'
            }`}
            data-testid={`insights-period-${p.id}`}
          >
            {p.id === 'custom' && <Calendar className="w-3.5 h-3.5" />}
            {p.label}
          </button>
        ))}
      </div>

      {period === 'custom' && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-6 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{t("insights.start_date")}</label>
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-200 focus:border-gold-500 outline-none"
              data-testid="insights-custom-start"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{t("insights.end_date")}</label>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-200 focus:border-gold-500 outline-none"
              data-testid="insights-custom-end"
            />
          </div>
          <span className="text-xs text-slate-400">{t("insights.pick_both")}</span>
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
          <Loader2 className="w-8 h-8 text-gold-500 animate-spin mx-auto mb-3" />
          <p className="text-slate-500 text-sm">{t("insights.loading")}</p>
        </div>
      ) : summary.orders === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center" data-testid="insights-empty">
          <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">{t("insights.empty")}</p>
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <StatCard icon={ShoppingBag} label={t("insights.orders")} value={summary.orders} color="blue" />
            <StatCard icon={DollarSign} label={t("insights.revenue")} value={`$${summary.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} color="green" />
            <StatCard icon={TrendingUp} label={t("insights.avg_order")} value={`$${summary.avg_order.toFixed(2)}`} color="gold" />
            <StatCard icon={Heart} label={t("insights.tips_total")} value={`$${summary.tips.toFixed(2)}`} sub={t("insights.tips_sub")} color="rose" />
            <StatCard icon={PieIcon} label={t("insights.subtotal")} value={`$${summary.subtotal.toFixed(2)}`} sub={t("insights.subtotal_sub")} color="purple" />
            <StatCard icon={DollarSign} label={t("insights.commission_saved")} value={`$${summary.commission_saved.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} sub={t("insights.commission_saved_sub")} color="gold" />
          </div>

          {/* Daily revenue chart */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-slate-900">{t("insights.sales_over_time")}</h3>
              <span className="text-xs text-slate-400">{t("insights.sales_legend")}</span>
            </div>
            <div className="w-full h-64" data-testid="insights-chart-daily">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={daily} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 }}
                    formatter={(val, name) => (name === 'revenue' ? [`$${Number(val).toFixed(2)}`, 'Revenue'] : [val, 'Orders'])}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line yAxisId="left" type="monotone" dataKey="orders" stroke="#0EA5E9" strokeWidth={2.5} dot={{ r: 3 }} name="Orders" />
                  <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#E0A830" strokeWidth={2.5} dot={{ r: 3 }} name="Revenue" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Top items */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <h3 className="font-bold text-slate-900 mb-3">{t("insights.top_items")}</h3>
              {topItems.length === 0 ? (
                <p className="text-sm text-slate-400 py-4">{t("insights.top_items_empty")}</p>
              ) : (
                <div className="space-y-3" data-testid="insights-top-items">
                  {topItems.map((it, idx) => (
                    <div key={it.name} className="flex items-center gap-3" data-testid={`insights-top-item-${idx}`}>
                      <div className="w-7 h-7 rounded-lg bg-gold-100 text-gold-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1 gap-2">
                          <span className="font-semibold text-slate-900 text-sm truncate">{it.name}</span>
                          <span className="text-xs text-slate-500 flex-shrink-0">{it.quantity} sold · ${it.revenue.toFixed(2)}</span>
                        </div>
                        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-gold-500 rounded-full transition-all" style={{ width: `${Math.max(2, it.percent)}%` }} />
                        </div>
                      </div>
                      <span className="text-xs font-semibold text-gold-700 w-12 text-right flex-shrink-0">{it.percent}%</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Order type breakdown */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <h3 className="font-bold text-slate-900 mb-3">{t("insights.delivery_vs_pickup")}</h3>
              {byType.length === 0 ? (
                <p className="text-sm text-slate-400 py-4">No data.</p>
              ) : (
                <div className="flex items-center gap-4" data-testid="insights-by-order-type">
                  <div className="w-44 h-44 flex-shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={byType}
                          dataKey="count"
                          nameKey="type"
                          innerRadius={45}
                          outerRadius={75}
                          paddingAngle={3}
                        >
                          {byType.map((entry, idx) => (
                            <Cell key={entry.type} fill={COLORS[idx % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 }}
                          formatter={(val, name) => [val, name]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 space-y-2">
                    {byType.map((t, idx) => (
                      <div key={t.type} className="flex items-center gap-2 text-sm">
                        <span className="w-3 h-3 rounded-full" style={{ background: COLORS[idx % COLORS.length] }} />
                        <span className="font-semibold capitalize text-slate-900 flex-1">{t.type}</span>
                        <span className="text-slate-500">{t.count} · ${t.revenue.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Hourly heatmap */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-slate-900 flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-400" /> {t("insights.peak_hours")}
              </h3>
              <span className="text-xs text-slate-400">{t("insights.peak_hours_sub")}</span>
            </div>
            <div className="grid grid-cols-12 sm:grid-cols-24 gap-1" data-testid="insights-by-hour">
              {byHour.map((h) => {
                const intensity = h.orders / maxHourOrders;
                const bg = h.orders === 0
                  ? '#f1f5f9'
                  : `rgba(224, 168, 48, ${0.18 + intensity * 0.82})`;
                return (
                  <div key={h.hour} className="flex flex-col items-center gap-1" title={`${h.hour}:00 — ${h.orders} orders · $${h.revenue.toFixed(2)}`}>
                    <div
                      className="w-full aspect-square rounded-md flex items-center justify-center text-[10px] font-bold text-slate-700"
                      style={{ background: bg }}
                    >
                      {h.orders > 0 ? h.orders : ''}
                    </div>
                    <span className="text-[10px] text-slate-400">{h.hour}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-3">{t('insights.peak_hours_hint')}</p>
          </div>
        </>
      )}
    </div>
  );
};

export default Insights;
